import BaseClient from '../../../services/api/client';

export const CollaborationPath = Object.freeze({
  ROOT: 'v1/collaborations',
  ATTACHMENTS: 'v1/collaboration-attachments',
});

const id = (value, label) => {
  if (value === undefined || value === null || value === '') {
    throw new TypeError(`${label} is required`);
  }
  return encodeURIComponent(String(value));
};

export class CollaborationService extends BaseClient {
  list(options = {}) {
    return this.get(CollaborationPath.ROOT, options);
  }

  detail(collaborationId, options = {}) {
    return this.get(`${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}`, options);
  }

  activeForPatient(patientId, options = {}) {
    return this.get(
      `${CollaborationPath.ROOT}/patients/${id(patientId, 'patientId')}/active`,
      options,
    );
  }

  searchDoctors(query = {}, options = {}) {
    const { q, mode, ...filters } = query;
    return this.get(`${CollaborationPath.ROOT}/doctors`, {
      ...options,
      query: {
        ...filters,
        query: q,
        mode: mode === 'around' ? 'radius' : mode,
      },
    });
  }

  searchPatients(query = {}, options = {}) {
    const { q, ...filters } = query;
    return this.get(`${CollaborationPath.ROOT}/patients`, { ...options, query: { ...filters, query: q } });
  }

  specialties(options = {}) {
    return this.get(`${CollaborationPath.ROOT}/specialties`, options);
  }

  create(values, options = {}) {
    return this.post(CollaborationPath.ROOT, values, options);
  }

  respond(collaborationId, decision, options = {}) {
    return this.post(
      `${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/invitation`,
      { decision },
      options,
    );
  }

  inviteDoctor(collaborationId, doctorId, options = {}) {
    return this.post(`${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/members`, { doctorId }, options);
  }

  removeDoctor(collaborationId, doctorId, options = {}) {
    return this.delete(`${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/members/${id(doctorId, 'doctorId')}`, options);
  }

  addNote(collaborationId, values, options = {}) {
    const body = values instanceof FormData ? values : this.noteFormData(values);
    return this.post(
      `${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/notes`,
      body,
      options,
    );
  }

  notes(collaborationId, before = null, options = {}) {
    return this.get(`${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/notes`, {
      ...options,
      query: before ? { before } : {},
    });
  }

  consultationNote(collaborationId, appointmentId, options = {}) {
    return this.get(
      `${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/appointments/${id(appointmentId, 'appointmentId')}/note`,
      options,
    );
  }

  saveConsultationNote(collaborationId, appointmentId, text, options = {}) {
    return this.put(
      `${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/appointments/${id(appointmentId, 'appointmentId')}/note`,
      { text },
      options,
    );
  }

  noteFormData({ text = '', appointmentId, clientRequestId, files = [] }) {
    const body = new FormData();
    if (text) body.append('text', text);
    if (appointmentId != null) body.append('appointmentId', String(appointmentId));
    body.append('clientRequestId', clientRequestId || crypto.randomUUID());
    Array.from(files).forEach((file) => body.append('attachments[]', file));
    return body;
  }

  leave(collaborationId, options = {}) {
    return this.post(
      `${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/leave`,
      {},
      options,
    );
  }

  archive(collaborationId, options = {}) {
    return this.post(
      `${CollaborationPath.ROOT}/${id(collaborationId, 'collaborationId')}/archive`,
      {},
      options,
    );
  }

  attachment(attachmentId, options = {}) {
    return this.get(
      `${CollaborationPath.ATTACHMENTS}/${id(attachmentId, 'attachmentId')}`,
      { ...options, responseType: 'blob' },
    );
  }

  attachmentUrl(url, options = {}) {
    return this.get(url, { ...options, responseType: 'blob' });
  }
}

export const collaborationService = new CollaborationService();

export default CollaborationService;
